import { CampingHrHeader } from "@/components/CampingHrHeader/CampingHrHeader.tsx";
import { Footer } from "@/components/Footer/Footer.tsx";
import { LegalModal } from "@/components/LegalModal/LegalModal.tsx";
import { ReservationOfferLoader } from "@/components/Loader/ReservationOfferLoader/ReservationOfferLoader.tsx";
import { MobileCartBar } from "@/components/MobileCartBar/MobileCartBar.tsx";
import { useGTMInit } from "@/helpers/gtm/useGTMInit.ts";
import { useGTMPage } from "@/helpers/gtm/useGTMPage.ts";
import { usePlitviceGTM } from "@/helpers/hooks/usePlitviceGTM.ts";
import { UtmSource } from "@/helpers/interfaces/UtmSource.ts";
import { AccommodationPicking } from "@/pages/AccommodationPicking/AccommodationPicking.tsx";
import { useURLParams } from "@/pages/AccommodationPicking/hooks/useURLParams.ts";
import { Header } from "@/pages/BookingEngine/Header/Header.tsx";
import { useCheckAndClearDataPreservation } from "@/pages/BookingEngine/hooks/useCheckAndClearDataPreservation.ts";
import { useHistorySync } from "@/pages/BookingEngine/hooks/useHistorySync.ts";
import { useHolderInfoForm } from "@/pages/BookingEngine/hooks/useHolderInfoForm.ts";
import { usePageScrollToTop } from "@/pages/BookingEngine/hooks/usePageScrollToTop.ts";
import { usePaymentForm } from "@/pages/BookingEngine/hooks/usePaymentForm.ts";
import { useRefreshCartTranslations } from "@/pages/BookingEngine/hooks/useRefreshCartTranslations.ts";
import { useResetCardOnNavigateBack } from "@/pages/BookingEngine/hooks/useResetCardOnNavigateBack.ts";
import { ExtraOptions } from "@/pages/ExtraOptions/ExtraOptions.tsx";
import { PaymentResult } from "@/pages/PaymentResult/PaymentResult.tsx";
import { HolderAndPaymentInfo } from "@/pages/ReservationHolderInformation/HolderAndPaymentInfo.tsx";
import { ReservationOverview } from "@/pages/ReservationOverview/ReservationOverview.tsx";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { AppPage } from "@/stores/EngineStore/enums/AppPage.ts";
import { RootAttribute } from "@/stores/EngineStore/enums/RootAttribute.ts";
import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

// Main component for rendering the correct page
// does not use router because this is an embedded app
export const BookingEngine = () => {
  const { page, rootAttributes, reservationOfferLoading, utmSource } = useEngineStore(
    useShallow((state) => ({
      page: state.page,
      rootAttributes: state.rootAttributes,
      reservationOfferLoading: state.reservationOfferLoading,
      utmSource: state.utmSource,
    })),
  );

  useURLParams();
  useCheckAndClearDataPreservation();
  useHistorySync();
  useGTMInit();
  useGTMPage();
  usePlitviceGTM();
  usePageScrollToTop();
  useRefreshCartTranslations();

  const { holderFormData } = useHolderInfoForm();
  const { paymentFormData } = usePaymentForm(holderFormData);
  useResetCardOnNavigateBack(paymentFormData.resetCreditCardFields);

  const hideHeader = useMemo(() => rootAttributes?.[RootAttribute.HideHeader], [rootAttributes]);

  if (reservationOfferLoading) {
    return <ReservationOfferLoader />;
  }

  return (
    <div className="bookingEngine o-booking-engine">
      {utmSource === UtmSource.CampingHr && <CampingHrHeader />}
      {!hideHeader && <Header />}
      <div className="bookingEnginePages" id="bookingEnginePages">
        {page === AppPage.AccommodationPicking && <AccommodationPicking />}
        {page === AppPage.ExtraOptions && <ExtraOptions />}
        {page === AppPage.HolderAndPaymentInfo && (
          <HolderAndPaymentInfo holderFormData={holderFormData} paymentFormData={paymentFormData} />
        )}
        {page === AppPage.ReservationOverview && (
          <ReservationOverview
            onSubmitComplete={() => {
              paymentFormData.reset();
              holderFormData.reset();
            }}
            onGoBack={paymentFormData.resetCreditCardFields}
          />
        )}
        {page === AppPage.PaymentResult && <PaymentResult />}
      </div>

      <Footer />
      <MobileCartBar holderFormData={holderFormData} paymentFormData={paymentFormData} />
      <LegalModal />
    </div>
  );
};
